import { memo, useCallback, useState } from 'react'

import { AutoDeleteOutlined } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { LifecyclePolicyDialog } from 'domains/Workspaces/Images'
import { useImageObjectDeleteMutation } from 'hooks/mutations/content'
import { imagesOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images'
import { imageRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Image'
import { imageObjectQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { imageId } = imageRoute.useParams()
  const navigate = useNavigate({ from: imageRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [dialog, setDialog] = useState(false)

  const imageQuery = useQuery(imageObjectQueryKeys.detail({ imageId }))
  const { data: image } = imageQuery

  const { mutate: deleteImageObject } = useImageObjectDeleteMutation()

  const handleDeleteImageObject = useCallback(() => {
    if (!image) return

    setAnchorEl(undefined)

    deleteImageObject(
      { imageId: image.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Image.nameDelete', { name: image.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: imagesOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [image, deleteImageObject, enqueueSnackbar, navigate, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={image?.name}
        entity={t('entity:content.image', { count: 1 })}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(undefined)

            setTimeout(() => {
              setDialog(true)
            }, 300)
          }}
        >
          <ListItemIcon>
            <AutoDeleteOutlined />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>{t('domain:Workspace.Image.setLifecyclePolicy', { count: 1 })}</Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={imageId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Workspace.Image.deleteImage', { count: 1 })}
          onConfirm={handleDeleteImageObject}
        />
      </ActionMenu>
      <Dialog fullWidth open={dialog} onClose={() => setDialog(false)}>
        {image ? <LifecyclePolicyDialog objectData={image} onSuccess={() => setDialog(false)} /> : null}
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
